import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PartyPopper, X, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLang } from "@/i18n/LanguageProvider";

const API = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = "ix.launch.toast.dismissed";
const COUNTRY_LABEL = {
  US: "United States", GB: "United Kingdom", NG: "Nigeria", GH: "Ghana",
  KE: "Kenya", ZA: "South Africa", SN: "Senegal", CM: "Cameroon",
  ET: "Ethiopia", CA: "Canada", BR: "Brazil", AU: "Australia",
  CH: "Switzerland", SE: "Sweden", NO: "Norway", DK: "Denmark",
  FI: "Finland", AT: "Austria", AE: "UAE", SA: "Saudi Arabia",
  IN: "India", PK: "Pakistan", BD: "Bangladesh", PH: "Philippines",
  ID: "Indonesia", VN: "Vietnam", TH: "Thailand", MY: "Malaysia",
  SG: "Singapore", JP: "Japan", KR: "South Korea", CN: "China",
  TR: "Turkey", MX: "Mexico", AR: "Argentina", CO: "Colombia",
  EG: "Egypt", MA: "Morocco", TN: "Tunisia", TZ: "Tanzania",
  UG: "Uganda", RW: "Rwanda", CI: "Côte d'Ivoire", ZM: "Zambia",
  ZW: "Zimbabwe", AO: "Angola", BW: "Botswana", NA: "Namibia",
  MZ: "Mozambique",
};

// Reads /api/corridors/launched and shows a one-time celebratory toast for
// the most recently launched country. Persisted per-country in localStorage
// so a visitor sees each launch exactly once.
export default function LaunchToast() {
  const { t } = useTranslation();
  const { prefix } = useLang();
  const [country, setCountry] = useState(null); // {code, label}

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/corridors/launched`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        const items = d?.items || [];
        if (!items.length) return;
        // Pick the most recent by launched_at
        items.sort((a, b) => String(b.launched_at || "").localeCompare(String(a.launched_at || "")));
        const latest = items[0];
        let dismissed = [];
        try {
          dismissed = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
        } catch (_) { /* ignore */ }
        if (dismissed.includes(latest.country)) return;
        const code = String(latest.country).toUpperCase();
        setCountry({ code, label: COUNTRY_LABEL[code] || code });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const dismiss = () => {
    if (!country) return;
    try {
      const dismissed = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
      if (!dismissed.includes(country.code)) dismissed.push(country.code);
      localStorage.setItem(LS_KEY, JSON.stringify(dismissed));
    } catch (_) { /* ignore */ }
    setCountry(null);
  };

  return (
    <AnimatePresence>
      {country && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.95 }}
          transition={{ duration: 0.45, ease: [0.76, 0, 0.24, 1] }}
          className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-40 max-w-sm"
          data-testid="launch-toast"
          role="status"
          aria-live="polite"
        >
          <div className="relative rounded-2xl bg-forest text-canvas p-4 pr-10 shadow-[0_20px_60px_-15px_rgba(45,13,94,0.6)] ring-1 ring-lime/40 overflow-hidden">
            {/* Ambient glow */}
            <div className="pointer-events-none absolute -right-14 -top-14 w-40 h-40 rounded-full bg-lime/20 blur-2xl" />
            <div className="pointer-events-none absolute -left-10 -bottom-10 w-32 h-32 rounded-full bg-[#7C3AED]/25 blur-2xl" />

            <div className="relative flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-lime text-forest grid place-items-center shrink-0">
                <PartyPopper className="w-5 h-5" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-lime">
                  {t("launchToast.eyebrow")}
                </div>
                <div
                  className="mt-1 font-display text-lg tracking-tight text-canvas leading-tight"
                  data-testid="launch-toast-headline"
                >
                  {t("launchToast.headline", { country: country.label })}
                </div>
                <div className="mt-1 text-xs text-canvas/70 leading-snug">
                  {t("launchToast.body", { country: country.label })}
                </div>
                <a
                  href={`${prefix("/waitlist")}?source=launch-toast-${country.code.toLowerCase()}`}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-lime text-forest text-xs font-semibold hover:bg-lime/90 transition-colors"
                  data-testid="launch-toast-cta"
                  onClick={dismiss}
                >
                  {t("launchToast.cta")}
                  <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.4} />
                </a>
              </div>
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label={t("launchToast.dismiss")}
              data-testid="launch-toast-dismiss"
              className="absolute right-2 top-2 w-7 h-7 rounded-full hover:bg-canvas/10 transition-colors grid place-items-center"
            >
              <X className="w-3.5 h-3.5" strokeWidth={2.4} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
